import { apiGet, apiPost } from './client';

export interface Website {
  id: string;
  customer_id: string;
  business_name: string;
  status: string;
  cloudflare_url: string | null;
  github_repo_url: string | null;
  template_used: string | null;
  custom_domain: string | null;
  domain_verification_status: string | null;
  tech_qa_score: Record<string, unknown> | null;
  tech_qa_passed_at: string | null;
  internal_qa_passed_at: string | null;
  created_at: string;
  launched_at: string | null;
  last_updated: string;
}

export interface Revision {
  id: string;
  source: 'customer' | 'internal-qa';
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'rejected';
  page: string | null;
  section: string | null;
  description: string;
  created_by: string;
  created_at: string;
  processed_at: string | null;
  /** Staff explanation shown to the customer. Required when status is 'rejected'. */
  staff_note: string | null;
}

interface GetSiteResponse {
  success: boolean;
  website: Website | null;
  message?: string;
}

interface GetRevisionsResponse {
  success: boolean;
  revisions: Revision[];
}

interface SubmitRevisionResponse {
  success: boolean;
  message: string;
  revisionId: string;
  websiteId: string;
  status: string;
}

interface ApproveSiteResponse {
  success: boolean;
  message: string;
  websiteId: string;
  status: string;
  previewUrl: string | null;
  customDomain: string | null;
  domainConnected: boolean;
}

export async function getSite(): Promise<GetSiteResponse> {
  return apiGet<GetSiteResponse>('/api/customer/site');
}

export async function getRevisions(): Promise<GetRevisionsResponse> {
  return apiGet<GetRevisionsResponse>('/api/customer/revisions');
}

export async function submitRevision(data: {
  page?: string;
  section?: string;
  description: string;
}): Promise<SubmitRevisionResponse> {
  return apiPost<SubmitRevisionResponse>('/api/customer/revisions', data);
}

export async function approveSite(): Promise<ApproveSiteResponse> {
  return apiPost<ApproveSiteResponse>('/api/customer/site/approve', {});
}

// Types for revision items
export type RevisionCategory = 'text' | 'images' | 'colors' | 'layout' | 'contact' | 'other';
export type RevisionLocation = 'header' | 'hero' | 'about' | 'services' | 'gallery' | 'testimonials' | 'contact' | 'footer' | 'general';

export interface RevisionItem {
  category: RevisionCategory;
  location?: RevisionLocation;
  description: string;
}

interface SubmitRevisionBatchResponse {
  success: boolean;
  message: string;
  revisionId: string;
  itemCount: number;
  websiteId: string;
  status: string;
}

export async function submitRevisionBatch(items: RevisionItem[]): Promise<SubmitRevisionBatchResponse> {
  return apiPost<SubmitRevisionBatchResponse>('/api/customer/revisions/batch', { items });
}

// Category labels for display
export const categoryLabels: Record<RevisionCategory, string> = {
  text: 'Text / Content',
  images: 'Images / Photos',
  colors: 'Colors / Styling',
  layout: 'Layout / Arrangement',
  contact: 'Contact Information',
  other: 'Other',
};

// Location labels for display (user-friendly terms)
export const locationLabels: Record<RevisionLocation, string> = {
  header: 'Top area (header/navigation)',
  hero: 'Main banner/welcome section',
  about: 'About section',
  services: 'Services section',
  gallery: 'Gallery/portfolio section',
  testimonials: 'Testimonials/reviews section',
  contact: 'Contact section',
  footer: 'Bottom of page (footer)',
  general: 'Entire page / General',
};

// Helper to format website status for display
export function formatStatus(status: string): string {
  const statusMap: Record<string, string> = {
    'pending-tech-qa': 'Technical QA',
    'pending-internal-qa': 'Internal Review',
    'pending-customer-review': 'Ready for Your Review',
    'needs-customer-revision': 'Revision in Progress',
    'approved-to-launch': 'Approved — Going Live Soon',
    'live': 'Live',
  };
  return statusMap[status] || status;
}

// Helper to get status color classes
export function getStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    'pending-tech-qa': 'bg-yellow-100 text-yellow-800',
    'pending-internal-qa': 'bg-yellow-100 text-yellow-800',
    'pending-customer-review': 'bg-blue-100 text-blue-800',
    'needs-customer-revision': 'bg-orange-100 text-orange-800',
    'approved-to-launch': 'bg-green-100 text-green-800',
    'live': 'bg-green-100 text-green-800',
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
}
